/**
 * Local quiz fixture for `VITE_USE_MOCK_QUIZ=true` — not bundled for behavior in production unless that flag is set at build time.
 */
import type { QuizConfig } from '../model/types';

/**
 * Deterministic quiz fixture when `VITE_USE_MOCK_QUIZ=true`: seven questions, four archetype results,
 * weights/labels valid for `winningResultFromScores`. `articleUrl` is echoed for session keys and analytics.
 */
export function getMockQuizConfig(articleUrl: string): QuizConfig {
  const results = [
    {
      name: 'Scholar/Seeker',
      description:
        'You find God in questions, study, and honest curiosity. Your path forward is rich with discovery—keep leaning in with an open heart.',
    },
    {
      name: 'Compassionate Servant',
      description:
        'Love shows up in your hands and habits. Small acts of care ripple outward—your faith is deeply lived alongside others.',
    },
    {
      name: 'Family Anchor',
      description:
        'Home, tradition, and the people you cherish ground your spirit. You nurture belonging, and that is sacred work.',
    },
    {
      name: 'Worshipful Heart',
      description:
        'You sense the holy in beauty, music, and stillness. Keep making space to behold—your wonder is a gift.',
    },
  ];

  const mk = (
    text: string,
    opts: { label: string; w: number; line: string }[],
  ): { text: string; answers: { text: string; resultLabel: string; weight: number }[] } => ({
    text,
    answers: opts.map((o) => ({ text: o.line, resultLabel: o.label, weight: o.w })),
  });

  return {
    quizTitle: 'Discover your faith style',
    articleUrl,
    questions: [
      mk('When you feel uncertain, what do you reach for first?', [
        { label: 'Scholar/Seeker', w: 3, line: 'Scripture, books, or trusted teaching' },
        { label: 'Compassionate Servant', w: 1, line: 'Someone who needs help beside me' },
        { label: 'Family Anchor', w: 1, line: 'The rhythms of home and people I love' },
      ]),
      mk('Sunday morning energy looks like…', [
        { label: 'Compassionate Servant', w: 3, line: 'Serving, greeting, making space for others' },
        { label: 'Worshipful Heart', w: 2, line: 'Music, beauty, lifting my heart in praise' },
        { label: 'Scholar/Seeker', w: 1, line: 'A sermon I can chew on all week' },
      ]),
      mk('Prayer, for you, is mostly…', [
        { label: 'Family Anchor', w: 4, line: 'Covering the people under my roof' },
        { label: 'Scholar/Seeker', w: 1, line: 'Honest questions laid before God' },
        { label: 'Worshipful Heart', w: 1, line: 'Stillness that feels like surrender' },
      ]),
      mk('You feel closest to God when…', [
        { label: 'Worshipful Heart', w: 3, line: 'Beauty stops you—music, nature, art' },
        { label: 'Family Anchor', w: 2, line: 'Traditions and stories are shared' },
        { label: 'Compassionate Servant', w: 1, line: 'Kindness heals a hard moment' },
      ]),
      mk('Your faith grows best through…', [
        { label: 'Scholar/Seeker', w: 2, line: 'Learning and new understanding' },
        { label: 'Compassionate Servant', w: 2, line: 'Showing up for people in need' },
        { label: 'Family Anchor', w: 2, line: 'Showing up consistently at home' },
      ]),
      mk('When life gets loud, you stabilize by…', [
        { label: 'Family Anchor', w: 3, line: 'Protecting peace for those you love' },
        { label: 'Worshipful Heart', w: 2, line: 'Finding a quiet corner to breathe' },
        { label: 'Scholar/Seeker', w: 1, line: 'Re-reading what has anchored you before' },
      ]),
      mk('The story you want to live is…', [
        { label: 'Compassionate Servant', w: 3, line: 'Love with sleeves rolled up' },
        { label: 'Worshipful Heart', w: 2, line: 'Wonder that spills into gratitude' },
        { label: 'Family Anchor', w: 1, line: 'Legacy built in ordinary days' },
      ]),
    ] as QuizConfig['questions'],
    results,
  };
}
